// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { reducers } from "@insite/client-framework/Store/Reducers";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";

export interface SystemBuilderProduct {
    id: string;
    productNumber: string;
    productTitle: string;
    smallImagePath: string;
    mediumImagePath: string;
    largeImagePath: string;
    imageAltText: string;
    productAttributeTypes: { label: string; values: string[] }[];
    qty: number;
    unitOfMeasure: string;
}

export interface System {
    ahri: string;
    condenserProductId: string;
    eer2: string;
    heating: null;
    hspf2: string;
    lowTempRating: number;
    nominalCooling: number;
    productIds: string[];
    products: SystemBuilderProduct[];
    rebateEligible: true;
    refrigerantType: string;
    seer2: string;
    rank: string;
    mapping: { [key: string]: string };
    sum?: number;
}

export interface UnitarySystem {
    brands: string[];
    tons: string[];
    indoorTypes: string[];
    furnaceBtus: string[];
    heatingMethods: string[];
    coilWidths: string[];
    kilowattRatings: string[];
    selectedBrand: string;
    selectedTons: string;
    selectedIndoorType: string;
    selectedFuranceBtus: string;
    selectedCoilWidth: string;
    selectedKilowattRatings: string;
    selectedHeatingMethod: string;
}

export interface MiniSplitConfigurations {
    selectedBrand: string;
    brands: string[];
    numberOfAvailableZones: string[];
    selectedNumberOfZones: string;
    miniSplitZones: { [zoneNumber: number]: MiniSplitZone };
    btuType: string; // cooling or heating
}

export interface MiniSplitZone {
    unitTypes: string[];
    btus: string[];
    selectedBtus: string;
    selectedUnitType: string;
}

export interface xSystemBuilderState {
    step: number;
    systemType?: "miniSplit" | "unitary";
    productsInfo?: { [erp: string]: ProductInfo };
    isPriceLoading?: boolean;
    isRelatedProductsModalOpen: boolean;
    condenserProductId?: string;

    relatedProducts: {
        [productId: string]: {
            products: ProductModel[];
            productsInfo: { [erp: string]: any };
        };
    };
    // used to save system builder parameters
    systemData?: any;

    filteredSystems?: System[];
    hasFilters?: boolean;

    // Unitary related state variables
    unitaryOutputsystems: System[];
    unitaryConfigurations: UnitarySystem;

    // Mini split related state variables
    miniSplitOutputsystems: System[];
    miniSplitConfugurations: MiniSplitConfigurations;
    disableViewSystemsButton: boolean;
}

const initialState: xSystemBuilderState = {
    step: 0,
    isRelatedProductsModalOpen: false,
    unitaryConfigurations: {
        brands: [],
        tons: [],
        indoorTypes: [],
        furnaceBtus: [],
        heatingMethods: ["Hydro Coil"],
        coilWidths: [],
        kilowattRatings: [],
        selectedBrand: "",
        selectedTons: "",
        selectedIndoorType: "",
        selectedFuranceBtus: "",
        selectedHeatingMethod: "",
        selectedCoilWidth: "",
        selectedKilowattRatings: "",
    },
    miniSplitConfugurations: {
        selectedBrand: "",
        brands: [],
        numberOfAvailableZones: [],
        selectedNumberOfZones: "",
        miniSplitZones: {},
        btuType: "cooling",
    },
    miniSplitOutputsystems: [],
    unitaryOutputsystems: [],
    systemData: [],
    filteredSystems: [],
    hasFilters: false,
    disableViewSystemsButton: true,
    productsInfo: {},
    isPriceLoading: false,
    condenserProductId: "",
    relatedProducts: {},
};

const reducer = {
    "xSystemBuilder/UpdateUnitarySystemObject": (
        draft: Draft<xSystemBuilderState>,
        action: { data: UnitarySystem },
    ) => {
        draft.unitaryConfigurations = action.data;
    },
    "xSystemBuilder/UpdateIsViewSystemsButtonDisabled": (
        draft: Draft<xSystemBuilderState>,
        action: { disableViewSystemsButton: boolean },
    ) => {
        draft.disableViewSystemsButton = action.disableViewSystemsButton;
    },
    "xSystemBuilder/ClearUnitarySystemObject": (draft: Draft<xSystemBuilderState>) => {
        draft.unitaryConfigurations = {
            ...draft.unitaryConfigurations,
            tons: [],
            indoorTypes: [],
            furnaceBtus: [],
            heatingMethods: [],
            coilWidths: [],
            kilowattRatings: [],
            selectedBrand: "",
            selectedTons: "",
            selectedIndoorType: "",
            selectedFuranceBtus: "",
            selectedHeatingMethod: "",
            selectedCoilWidth: "",
            selectedKilowattRatings: "",
        };
    },
    "xSystemBuilder/SetStep": (
        draft: Draft<xSystemBuilderState>,
        action: { step: number; systemType: "miniSplit" | "unitary" },
    ) => {
        draft.step = action.step;
        if (action.systemType) {
            draft.systemType = action.systemType;
        }
        // clear related products
        draft.relatedProducts = {};
    },
    "xSystemBuilder/SetSystems": (
        draft: Draft<xSystemBuilderState>,
        action: { systems: System[]; systemData: any; filteredSystems: System[]; hasFilters: boolean },
    ) => {
        draft.systemData = action.systemData;
        draft.filteredSystems = action.filteredSystems;
        draft.hasFilters = action.hasFilters;
        if (draft.systemType === "unitary") {
            draft.unitaryOutputsystems = action.systems;
            return;
        }
        draft.miniSplitOutputsystems = action.systems;
    },
    "xSystemBuilder/UpdateMiniSplitZoneInfo": (
        draft: Draft<xSystemBuilderState>,
        action: { zoneNumber: number; zoneInfo: MiniSplitZone },
    ) => {
        draft.miniSplitConfugurations = {
            ...draft.miniSplitConfugurations,
            miniSplitZones: { ...draft.miniSplitConfugurations.miniSplitZones, [action.zoneNumber]: action.zoneInfo },
        };

        // check for disable or enable view systems button
        if (
            Object.keys(draft.miniSplitConfugurations.miniSplitZones)?.length ===
                Number(draft.miniSplitConfugurations.selectedNumberOfZones) &&
            Object.values(draft.miniSplitConfugurations.miniSplitZones).every(
                zone => !!zone.selectedBtus && !!zone.selectedUnitType,
            )
        ) {
            draft.disableViewSystemsButton = false;
        } else {
            draft.disableViewSystemsButton = true;
        }
    },
    "xSystemBuilder/MiniSplitSetBtuType": (draft: Draft<xSystemBuilderState>, action: { btuType: string }) => {
        draft.miniSplitConfugurations.btuType = action.btuType;
    },
    "xSystemBuilder/SetCondenserProductId": (draft: Draft<xSystemBuilderState>, action: { productId: string }) => {
        draft.condenserProductId = action.productId;
    },
    "xSystemBuilder/UpdateMiniSplitConfigurationObject": (
        draft: Draft<xSystemBuilderState>,
        action: { miniSplitConfugurations: MiniSplitConfigurations },
    ) => {
        draft.miniSplitConfugurations = { ...action.miniSplitConfugurations };
    },
    "xSystemBuilder/ClearMiniSplitConfigurationObject": (draft: Draft<xSystemBuilderState>) => {
        draft.miniSplitConfugurations = {
            ...draft.miniSplitConfugurations,
            selectedBrand: "",
            numberOfAvailableZones: [],
            selectedNumberOfZones: "",
            miniSplitZones: {},
            btuType: "cooling",
        };
        draft.disableViewSystemsButton = true;
    },

    "xSystemBuilder/BeginLoadProductsInfo": (draft: Draft<xSystemBuilderState>) => {
        draft.isPriceLoading = true;
    },

    "xSystemBuilder/CompleteLoadProductsInfo": (
        draft: Draft<xSystemBuilderState>,
        action: { productsInfo: { [erp: string]: ProductInfo } },
    ) => {
        // inject productInfos inside each system products
        draft.productsInfo = action.productsInfo;
        draft.isPriceLoading = false;

        // calculate sum of price for each system
        if (draft.systemType === "miniSplit") {
            draft.miniSplitOutputsystems?.forEach(system => {
                let sum = 0;

                system?.products?.forEach(product => {
                    if (action.productsInfo[product.id]) {
                        sum += action.productsInfo?.[product.id]?.pricing?.extendedUnitNetPrice || 0;
                    }
                });
                system.sum = sum;
            });
        } else {
            draft.unitaryOutputsystems?.forEach(system => {
                let sum = 0;

                system?.products?.forEach(product => {
                    if (action.productsInfo[product.id]) {
                        sum += action.productsInfo[product.id].pricing?.extendedUnitNetPrice || 0;
                    }
                });
                system.sum = sum;
            });
        }
    },
    "xSystemBuilder/UpdateIsModalOpen": (draft: Draft<xSystemBuilderState>, action: { isModalOpen: boolean }) => {
        draft.isRelatedProductsModalOpen = action.isModalOpen;
    },
    "xSystemBuilder/CompleteLoadModalProducts": (
        draft: Draft<xSystemBuilderState>,
        action: { products: ProductModel[]; key: string },
    ) => {
        if (!draft.relatedProducts) {
            draft.relatedProducts = {};
        }
        draft.relatedProducts[action.key] = {
            ...draft.relatedProducts[action.key],
            products: [...(draft.relatedProducts[action.key]?.products || []), ...action.products],
        };
    },
    "xSystemBuilder/CompleteLoadModalProductsInfo": (
        draft: Draft<xSystemBuilderState>,
        action: { productsInfo: { [erp: string]: any }; key: string },
    ) => {
        if (!draft.relatedProducts) {
            draft.relatedProducts = {};
        }
        draft.relatedProducts[action.key] = {
            ...draft.relatedProducts[action.key],
            productsInfo: {
                ...(draft.relatedProducts[action.key].productsInfo || {}),
                ...action.productsInfo,
            },
        };
    },
    "xSystemBuilder/ClearAllSystems": () => {
        // reset whole state
        return initialState;
    },
};

const xSystemBuilder = createTypedReducerWithImmer(initialState, reducer);
(reducers as any).xSystemBuilder = xSystemBuilder;

export type xSystemBuilder = {
    xSystemBuilder: xSystemBuilderState;
} & ApplicationState;

type xSystemBuilderReducers = {
    [key: string]: typeof xSystemBuilder;
};

export type xSystemBuilderActions = Parameters<xSystemBuilderReducers[keyof xSystemBuilderReducers]>[1];
